<template>
  <div
    ref="workspace"
    :class="['restaurante-producao', { 'is-fullscreen': isFullscreen }]"
  >
    <section
      class="card card-custom kds-shell"
      :aria-busy="painelLoading ? 'true' : 'false'"
    >
      <h1 class="sr-only">{{ titulo }} — painel de produção</h1>

      <div class="kds-commandbar">
        <div class="kds-station">
          <span class="kds-station-indicator" aria-hidden="true"></span>
          <div>
            <strong>{{ titulo }}</strong>
            <small>{{ totalTicketsLabel }}</small>
          </div>
          <span v-if="atrasadosTickets" class="kds-overdue-pill">
            <i class="fas fa-exclamation-triangle" aria-hidden="true"></i>
            {{ overdueTicketsLabel }}
          </span>
        </div>

        <div class="kds-controls" aria-label="Controles da produção">
          <label class="kds-sector-filter" for="setor_producao_id">
            <i class="fas fa-filter" aria-hidden="true"></i>
            <span class="sr-only">Filtrar por setor</span>
            <select
              id="setor_producao_id"
              v-model="filters.setor_producao_id"
              @change="getPainel"
            >
              <option
                v-for="option in setorOptions"
                :key="String(option.value)"
                :value="option.value"
              >
                {{ option.text }}
              </option>
            </select>
          </label>

          <button
            type="button"
            class="kds-auto-refresh"
            :class="{ active: autoRefresh }"
            :aria-pressed="autoRefresh ? 'true' : 'false'"
            @click="autoRefresh = !autoRefresh"
          >
            <span class="kds-live-dot" aria-hidden="true"></span>
            {{ autoRefresh ? "Tempo real" : "Pausado" }}
          </button>

          <span class="kds-last-refresh">{{ lastRefreshLabel }}</span>

          <router-link
            v-if="exibirMesasAtalho"
            class="kds-icon-button"
            :to="{ name: 'restauranteMesas' }"
            title="Abrir mesas"
            aria-label="Abrir mesas"
          >
            <i class="fas fa-chair" aria-hidden="true"></i>
          </router-link>

          <button
            type="button"
            class="kds-icon-button"
            :disabled="painelLoading"
            title="Atualizar produção"
            aria-label="Atualizar produção"
            @click="getPainel"
          >
            <i
              class="fas fa-sync-alt"
              :class="{ 'fa-spin': painelLoading }"
              aria-hidden="true"
            ></i>
          </button>

          <button
            type="button"
            class="kds-icon-button kds-fullscreen-button"
            :title="isFullscreen ? 'Sair da tela cheia' : 'Usar tela cheia'"
            :aria-label="
              isFullscreen ? 'Sair da tela cheia' : 'Usar tela cheia'
            "
            @click="toggleFullscreen"
          >
            <i
              :class="['fas', isFullscreen ? 'fa-compress' : 'fa-expand']"
              aria-hidden="true"
            ></i>
          </button>
        </div>
      </div>

      <nav
        v-if="!restauranteProducaoEtapaUnica"
        class="kds-stage-tabs"
        aria-label="Etapas da produção"
      >
        <button
          v-for="column in statusColumns"
          :key="column.status"
          type="button"
          :class="[
            `is-${column.status}`,
            { active: activeColumn === column.status },
          ]"
          :aria-current="activeColumn === column.status ? 'page' : null"
          @click="activeColumn = column.status"
        >
          {{ column.label }}
          <span>{{ ticketsPorColuna(column).length }}</span>
        </button>
      </nav>

      <main
        :class="[
          'kds-board',
          { 'kds-board-etapa-unica': restauranteProducaoEtapaUnica },
        ]"
      >
        <div
          v-for="column in statusColumns"
          :key="column.status"
          :class="[
            'kds-column',
            `is-${column.status}`,
            { 'is-mobile-active': activeColumn === column.status },
          ]"
        >
          <div :class="['kds-column-header', `kds-column-${column.status}`]">
            <div>
              <strong>{{ column.label }}</strong>
              <small>{{ columnHelp(column.status) }}</small>
            </div>
            <div class="kds-column-counts">
              <strong>{{ ticketsPorColuna(column).length }}</strong>
              <span>{{ itemCountLabel(itensPorColuna(column).length) }}</span>
            </div>
          </div>

          <div class="kds-column-scroll">
            <div
              v-if="!ticketsPorColuna(column).length"
              class="kds-column-empty"
            >
              <i class="far fa-check-circle" aria-hidden="true"></i>
              <div>
                <strong>Nenhum ticket nesta etapa</strong>
                <span>A próxima movimentação aparecerá aqui.</span>
              </div>
            </div>

            <article
              v-for="ticket in ticketsPorColuna(column)"
              :key="ticket.key"
              :class="[
                'producao-ticket',
                `producao-card-${ticket.status}`,
                {
                  'producao-card-atencao': ticket.atencao,
                  'producao-card-atrasado': ticket.atrasado,
                },
              ]"
            >
              <button
                type="button"
                class="producao-ticket-header"
                :aria-expanded="isTicketExpanded(ticket) ? 'true' : 'false'"
                @click="toggleTicket(ticket)"
              >
                <div class="producao-ticket-identity">
                  <span>{{ itemTicketLabel(ticket.firstItem) }}</span>
                  <strong>{{ itemMesaLabel(ticket.firstItem) }}</strong>
                  <small>{{ itemSetorLabel(ticket.firstItem) }}</small>
                </div>
                <div class="producao-ticket-badges">
                  <span class="producao-ticket-summary">
                    {{ itemCountLabel(ticket.items.length) }}
                  </span>
                  <span
                    class="kds-timer"
                    :class="{
                      'is-warning': ticket.atencao,
                      'is-danger': ticket.atrasado,
                    }"
                  >
                    <i class="far fa-clock" aria-hidden="true"></i>
                    {{ tempoDecorridoLabel(ticket.firstItem) }}
                  </span>
                </div>
                <i
                  :class="[
                    'fas',
                    isTicketExpanded(ticket)
                      ? 'fa-chevron-up'
                      : 'fa-chevron-down',
                  ]"
                  aria-hidden="true"
                ></i>
              </button>

              <div class="producao-ticket-meta">
                <span>
                  Enviado {{ formatDate(ticket.firstItem.enviado_at) }}
                </span>
              </div>

              <div
                v-if="ticketNextStatus(ticket)"
                class="producao-ticket-actions"
              >
                <button
                  type="button"
                  class="btn font-weight-bold"
                  :class="ticketActionClass(ticketNextStatus(ticket))"
                  :disabled="isTicketUpdating(ticket)"
                  @click="
                    updateTicketGroupStatus(ticket, ticketNextStatus(ticket))
                  "
                >
                  <i
                    v-if="isTicketUpdating(ticket)"
                    class="fas fa-spinner fa-spin"
                    aria-hidden="true"
                  ></i>
                  <span>{{ actionLabel(ticketNextStatus(ticket), true) }}</span>
                  <i
                    v-if="!isTicketUpdating(ticket)"
                    class="fas fa-arrow-right producao-action-arrow"
                    aria-hidden="true"
                  ></i>
                </button>
              </div>

              <div
                v-if="isTicketExpanded(ticket)"
                class="producao-ticket-items"
              >
                <div
                  v-for="grupo in ticket.comandas"
                  :key="grupo.key"
                  class="producao-comanda-group"
                >
                  <div class="producao-comanda-header">
                    <strong>{{ grupo.label }}</strong>
                    <span>{{ itemCountLabel(grupo.items.length) }}</span>
                  </div>

                  <div
                    v-for="item in grupo.items"
                    :key="item.id"
                    class="producao-ticket-item"
                  >
                    <div class="producao-produto">
                      <strong>{{ produtoDescricao(item) }}</strong>
                      <span>{{ quantidadeItem(item) }}</span>
                    </div>

                    <div
                      v-if="observacaoLinhas(item).length"
                      class="producao-observacao"
                    >
                      <span
                        v-for="(linha, linhaIndex) in observacaoLinhas(item)"
                        :key="linhaIndex"
                      >
                        {{ linha }}
                      </span>
                    </div>

                    <div class="producao-actions">
                      <button
                        v-if="nextStatus(item.status)"
                        type="button"
                        class="btn btn-sm btn-light-primary font-weight-bold"
                        :disabled="isItemUpdating(item)"
                        @click="updateStatus(item, nextStatus(item.status))"
                      >
                        <i
                          v-if="isItemUpdating(item)"
                          class="fas fa-spinner fa-spin"
                          aria-hidden="true"
                        ></i>
                        {{ actionLabel(nextStatus(item.status)) }}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </div>
      </main>

      <div class="sr-only" aria-live="polite" aria-atomic="true">
        {{ liveMessage }}
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { restauranteFeatureFlags, restauranteTempoLimites } from "../../config";
import { imprimirTicketsProducao } from "../../services/qzTrayPrinter";
import { formatElapsedMinutes, quantityLabel } from "./kdsWorkspace";

export default {
  name: "app-restaurante-producao",
  props: {
    tipoProducao: {
      type: String,
      default: null,
    },
    titulo: {
      type: String,
      default: "Produção",
    },
    subtitulo: {
      type: String,
      default: "Cozinha e bar",
    },
    exibirMesasAtalho: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      itens: [],
      setores: [],
      autoRefresh: true,
      refreshInterval: null,
      impressaoInterval: null,
      imprimindoPendentes: false,
      impressaoErroNotificadoAt: null,
      lastRefreshAt: null,
      featureFlags: restauranteFeatureFlags,
      restauranteProducaoEtapaUnica: false,
      restauranteUsarApenasComandas: false,
      expandedTickets: {},
      activeColumn: "pendente",
      painelLoading: false,
      requestSequence: 0,
      updatingTicketKeys: {},
      updatingItemIds: {},
      liveMessage: "",
      visibilityHandler: null,
      focusHandler: null,
      fullscreenHandler: null,
      isFullscreen: false,
      filters: {
        setor_producao_id: null,
      },
    };
  },
  computed: {
    ...mapGetters(["itsblocked"]),
    statusColumns() {
      if (this.restauranteProducaoEtapaUnica) {
        return [{ status: "pronto", label: "Produção" }];
      }

      return [
        { status: "pendente", label: "Pendente" },
        { status: "preparando", label: "Preparando" },
        { status: "pronto", label: "Pronto" },
      ];
    },
    setorOptions() {
      return [
        { value: null, text: "Todos" },
        ...this.setores
          .filter(
            (setor) => !this.tipoProducao || setor.tipo === this.tipoProducao
          )
          .map((setor) => ({
            value: setor.id,
            text: `${setor.nome} (${this.tipoLabel(setor.tipo)})`,
          })),
      ];
    },
    allTickets() {
      return this.statusColumns.flatMap((column) =>
        this.ticketsPorColuna(column)
      );
    },
    totalTicketsLabel() {
      return `${quantityLabel(
        this.allTickets.length,
        "ticket",
        "tickets"
      )} no painel`;
    },
    atrasadosTickets() {
      return this.allTickets.filter((ticket) => ticket.atrasado).length;
    },
    overdueTicketsLabel() {
      return quantityLabel(this.atrasadosTickets, "atrasado", "atrasados");
    },
    lastRefreshLabel() {
      if (!this.lastRefreshAt) {
        return "aguardando primeira carga";
      }

      return `Atualizado ${this.lastRefreshAt.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      })}`;
    },
  },
  mounted() {
    if (this.itsblocked) {
      this.$router.push({ name: "assinatura" });
      return;
    }

    this.getInit();
    this.visibilityHandler = () => {
      if (!document.hidden && this.autoRefresh) {
        this.getPainel(false);
        this.imprimirPendentesProducao();
      }
    };
    this.focusHandler = () => {
      if (!document.hidden && this.autoRefresh) {
        this.getPainel(false);
      }
    };
    this.fullscreenHandler = () => {
      this.isFullscreen =
        document.fullscreenElement === document.documentElement;
    };
    document.addEventListener("visibilitychange", this.visibilityHandler);
    document.addEventListener("fullscreenchange", this.fullscreenHandler);
    window.addEventListener("focus", this.focusHandler);
    this.fullscreenHandler();
    this.refreshInterval = setInterval(() => {
      // Pausa polling/impressao quando a aba esta ociosa (evita carga no
      // backend e impressoes de uma tela que ninguem ve).
      if (this.autoRefresh && !document.hidden) {
        this.getPainel(false);
      }
    }, 15000);
    this.impressaoInterval = setInterval(() => {
      if (this.autoRefresh && !document.hidden) {
        this.imprimirPendentesProducao();
      }
    }, 10000);
  },
  beforeDestroy() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
    if (this.impressaoInterval) {
      clearInterval(this.impressaoInterval);
    }
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
    }
    if (this.fullscreenHandler) {
      document.removeEventListener("fullscreenchange", this.fullscreenHandler);
    }
    if (this.focusHandler) {
      window.removeEventListener("focus", this.focusHandler);
    }
  },
  methods: {
    async toggleFullscreen() {
      try {
        if (document.fullscreenElement === document.documentElement) {
          await document.exitFullscreen();
          return;
        }

        if (!document.documentElement?.requestFullscreen) {
          this.$toast.warning(
            "A tela cheia não está disponível neste navegador."
          );
          return;
        }

        await document.documentElement.requestFullscreen();
      } catch (error) {
        this.$toast.warning("Não foi possível abrir o KDS em tela cheia.");
      }
    },
    async getInit() {
      this.painelLoading = true;
      try {
        await this.getConfiguracoesRestaurante();
        await Promise.all([this.getSetores(), this.getPainel(false)]);
        await this.imprimirPendentesProducao();
      } finally {
        this.painelLoading = false;
      }
    },
    async getConfiguracoesRestaurante() {
      try {
        const { data } = await this.$http.post("/configuracoesgerais/find");
        const configuracoes = Array.isArray(data) ? data[0] : data;

        this.restauranteProducaoEtapaUnica =
          configuracoes?.restaurante_producao_etapa_unica ?? false;
        this.restauranteUsarApenasComandas =
          configuracoes?.restaurante_usar_apenas_comandas ?? false;

        if (this.restauranteProducaoEtapaUnica) {
          this.activeColumn = "pronto";
        }
      } catch (error) {
        this.restauranteProducaoEtapaUnica = false;
        this.restauranteUsarApenasComandas = false;
      }
    },
    async getSetores() {
      try {
        const { data } = await this.$http.post(
          "/restaurante/setores-producao/select"
        );
        this.setores = data;
      } catch (error) {
        this.$toast.error("Não foi possível carregar os setores.");
      }
    },
    async getPainel(showLoading = true) {
      const requestId = ++this.requestSequence;
      try {
        if (showLoading) {
          this.painelLoading = true;
        }

        const payload = {};

        if (this.filters.setor_producao_id) {
          payload.setor_producao_id = this.filters.setor_producao_id;
        }

        if (this.tipoProducao) {
          payload.tipo = this.tipoProducao;
        }

        const { data } = await this.$http.post(
          "/restaurante/producao/painel",
          payload
        );
        if (requestId !== this.requestSequence) {
          return;
        }

        this.itens = Array.isArray(data) ? data : [];
        this.lastRefreshAt = new Date();
        this.liveMessage = `Produção atualizada: ${this.totalTicketsLabel}.`;
        this.imprimirPendentesProducao();
      } catch (error) {
        this.$toast.error("Não foi possível carregar a produção.");
      } finally {
        if (showLoading && requestId === this.requestSequence) {
          this.painelLoading = false;
        }
      }
    },
    async updateStatus(item, status) {
      if (this.isItemUpdating(item)) return;
      this.$set(this.updatingItemIds, item.id, true);
      try {
        await this.$http.post("/restaurante/producao/update-status", {
          id: item.id,
          status,
        });
        const message = `${this.produtoDescricao(item)}: ${this.actionLabel(
          status
        ).toLowerCase()}.`;
        this.$toast.success(message);
        this.liveMessage = message;
        await this.getPainel(false);
      } catch (error) {
        const message =
          error.response && error.response.data && error.response.data.msg
            ? error.response.data.msg
            : "Não foi possível atualizar o status.";
        this.$toast.error(message);
      } finally {
        this.$delete(this.updatingItemIds, item.id);
      }
    },
    async imprimirPendentesProducao() {
      if (this.imprimindoPendentes) {
        return;
      }

      this.imprimindoPendentes = true;

      try {
        const payload = {
          limite: 20,
        };

        if (this.tipoProducao) {
          payload.tipo = this.tipoProducao;
        }

        if (this.filters.setor_producao_id) {
          payload.setor_producao_id = this.filters.setor_producao_id;
        }

        const { data } = await this.$http.post(
          "/restaurante/producao/impressao-pendente",
          payload
        );

        if (!Array.isArray(data) || !data.length) {
          return;
        }

        // So marca como impressos os tickets efetivamente enviados a impressora.
        // Antes enviava todos os ids (inclusive os sem impressora/conteudo),
        // fazendo pedidos "sumirem" da fila sem serem impressos.
        const idsImpressos = await imprimirTicketsProducao(data);

        if (idsImpressos.length) {
          await this.$http.post("/restaurante/producao/marcar-impresso", {
            ids: idsImpressos,
          });
        }
      } catch (error) {
        this.notificarErroImpressao(error);
      } finally {
        this.imprimindoPendentes = false;
      }
    },
    notificarErroImpressao(error) {
      const now = Date.now();

      if (
        this.impressaoErroNotificadoAt &&
        now - this.impressaoErroNotificadoAt < 60000
      ) {
        return;
      }

      this.impressaoErroNotificadoAt = now;
      this.$toast.warning(
        error.message ||
          "Existem pedidos pendentes, mas não foi possível imprimir no QZ Tray."
      );
    },
    itensPorStatus(status) {
      return this.itens.filter((item) => item.status === status);
    },
    itensPorColuna(column) {
      if (this.restauranteProducaoEtapaUnica) {
        return this.itens;
      }

      return this.itensPorStatus(column.status);
    },
    ticketsPorColuna(column) {
      const tickets = {};

      this.itensPorColuna(column).forEach((item) => {
        const key = `${
          this.restauranteProducaoEtapaUnica ? "etapa-unica" : column.status
        }:${this.ticketKey(item)}`;

        if (!tickets[key]) {
          tickets[key] = {
            key,
            status: item.status,
            firstItem: item,
            items: [],
            atencao: false,
            atrasado: false,
          };
        }

        tickets[key].items.push(item);

        if (
          this.itemTimestamp(item) < this.itemTimestamp(tickets[key].firstItem)
        ) {
          tickets[key].firstItem = item;
        }

        tickets[key].atencao = tickets[key].atencao || this.isItemAtencao(item);
        tickets[key].atrasado =
          tickets[key].atrasado || this.isItemAtrasado(item);
      });

      const ticketsOrdenados = Object.values(tickets).map((ticket) => ({
        ...ticket,
        status: this.ticketStatus(ticket.items),
        items: ticket.items.sort(
          (first, second) =>
            this.itemTimestamp(first) - this.itemTimestamp(second)
        ),
        comandas: this.groupTicketItemsByComanda(ticket.items),
      }));

      return ticketsOrdenados.sort((first, second) => {
        const firstTimestamp = this.itemTimestamp(first.firstItem);
        const secondTimestamp = this.itemTimestamp(second.firstItem);
        const mostrarRecentesPrimeiro =
          !this.restauranteProducaoEtapaUnica && column.status === "pronto";

        return mostrarRecentesPrimeiro
          ? secondTimestamp - firstTimestamp
          : firstTimestamp - secondTimestamp;
      });
    },
    ticketStatus(items) {
      const itemEmPreparo = items.find((item) => item.status !== "pronto");

      return itemEmPreparo ? itemEmPreparo.status : "pronto";
    },
    ticketNextStatus(ticket) {
      if (this.restauranteProducaoEtapaUnica) {
        return ticket.items.some((item) => item.status !== "pronto")
          ? "pronto"
          : "entregue";
      }

      return this.nextStatus(ticket.status);
    },
    nextStatus(status) {
      const next = {
        pendente: "preparando",
        preparando: "pronto",
        pronto: "entregue",
      };

      return next[status] || null;
    },
    ticketKey(item) {
      return [
        item.atendimento_id || "atendimento",
        item.mesa?.id || item.mesa_id || "sem-mesa",
        item.setor_producao_id || item.setor_producao?.id || "sem-setor",
        this.ticketBatchKey(item),
      ].join(":");
    },
    ticketBatchKey(item) {
      const value =
        item.ticket_key ||
        item.pedido_producao_id ||
        item.lote_producao_id ||
        item.enviado_at ||
        item.item?.enviado_producao_at ||
        item.created_at ||
        item.id;

      return String(value || item.id || "sem-envio")
        .replace("T", " ")
        .slice(0, 19);
    },
    itemComandaKey(item) {
      return (
        item.comanda?.id ||
        item.item?.comanda?.id ||
        item.comanda_id ||
        item.item?.comanda_id ||
        "sem-comanda"
      );
    },
    itemComandaLabel(item) {
      const comanda = item.comanda || item.item?.comanda || null;

      if (this.restauranteUsarApenasComandas && comanda?.tipo === "avulso") {
        return this.comandaModoLabel(comanda);
      }

      if (comanda?.tipo === "avulso" || (!comanda?.nome && comanda?.codigo)) {
        return `Avulsa ${comanda.codigo || comanda.id}`;
      }

      const nome =
        item.comanda?.nome ||
        item.item?.comanda?.nome ||
        item.item?.comanda_nome ||
        "";
      const numero =
        item.comanda?.numero ||
        item.item?.comanda?.numero ||
        item.comanda_numero ||
        "";

      if (nome) {
        return nome;
      }

      return numero ? `Comanda ${numero}` : "Sem comanda";
    },
    comandaModoLabel(comanda) {
      const nome = String(comanda?.nome || "").trim();
      const codigo = String(comanda?.codigo || "").trim();

      if (nome && codigo) {
        return `Comanda ${nome} (${codigo})`;
      }

      if (nome) {
        return `Comanda ${nome}`;
      }

      if (codigo) {
        return `Comanda ${codigo}`;
      }

      return comanda?.id ? `Comanda #${comanda.id}` : "Comanda";
    },
    groupTicketItemsByComanda(items) {
      const grupos = {};

      items.forEach((item) => {
        const key = this.itemComandaKey(item);

        if (!grupos[key]) {
          grupos[key] = {
            key,
            label: this.itemComandaLabel(item),
            items: [],
          };
        }

        grupos[key].items.push(item);
      });

      return Object.values(grupos);
    },
    isTicketExpanded(ticket) {
      if (
        Object.prototype.hasOwnProperty.call(this.expandedTickets, ticket.key)
      ) {
        return this.expandedTickets[ticket.key];
      }

      return ticket.status !== "pronto";
    },
    toggleTicket(ticket) {
      this.$set(
        this.expandedTickets,
        ticket.key,
        !this.isTicketExpanded(ticket)
      );
    },
    async updateTicketGroupStatus(ticket, status) {
      if (this.isTicketUpdating(ticket)) return;
      this.$set(this.updatingTicketKeys, ticket.key, true);
      try {
        const itens = ticket.items.filter((ticketItem) =>
          this.canTransitionTo(ticketItem.status, status)
        );

        if (!itens.length) {
          return;
        }

        if (this.featureFlags.kdsAvancado) {
          try {
            await this.$http.post("/restaurante/producao/bump", {
              ticket_key: ticket.key,
              ids: itens.map((ticketItem) => ticketItem.id),
              status,
            });
          } catch (error) {
            await this.updateTicketStatusFallback(itens, status);
          }
        } else {
          await this.updateTicketStatusFallback(itens, status);
        }

        const message = `${this.itemMesaLabel(
          ticket.firstItem
        )}: ${this.actionLabel(status, true).toLowerCase()}.`;
        this.$toast.success(message);
        this.liveMessage = message;
        await this.getPainel(false);
      } catch (error) {
        const message =
          error.response && error.response.data && error.response.data.msg
            ? error.response.data.msg
            : "Não foi possível atualizar o ticket.";
        this.$toast.error(message);
      } finally {
        this.$delete(this.updatingTicketKeys, ticket.key);
      }
    },
    updateTicketStatusFallback(itens, status) {
      return Promise.all(
        itens.map((ticketItem) =>
          this.$http.post("/restaurante/producao/update-status", {
            id: ticketItem.id,
            status,
          })
        )
      );
    },
    canTransitionTo(currentStatus, targetStatus) {
      const transitions = {
        pendente: ["preparando", "pronto"],
        preparando: ["pronto"],
        pronto: ["entregue"],
      };

      return (transitions[currentStatus] || []).includes(targetStatus);
    },
    itemMesaLabel(item) {
      if (item.mesa) {
        return `Mesa ${item.mesa.numero}`;
      }

      const comanda = item.item?.comanda || item.comanda;

      if (this.restauranteUsarApenasComandas && comanda?.tipo === "avulso") {
        return this.comandaModoLabel(comanda);
      }

      if (comanda?.tipo === "avulso" || comanda?.codigo) {
        return `Avulsa ${comanda.codigo || comanda.id}`;
      }

      return "Mesa não informada";
    },
    itemSetorLabel(item) {
      return item.setor_producao ? item.setor_producao.nome : "Sem setor";
    },
    itemTicketLabel(item) {
      const atendimento = item.atendimento_id
        ? `Atendimento #${item.atendimento_id}`
        : "Atendimento";

      return atendimento;
    },
    minutosDecorridos(item) {
      const value = this.itemStageDate(item);

      if (!value) {
        return 0;
      }

      const startedAt = new Date(value);
      const diff = Date.now() - startedAt.getTime();
      return Number.isFinite(diff) ? Math.max(diff / 60000, 0) : 0;
    },
    tempoDecorridoLabel(item) {
      return formatElapsedMinutes(this.minutosDecorridos(item));
    },
    isItemAtencao(item) {
      return (
        this.minutosDecorridos(item) >=
        restauranteTempoLimites.producaoAtencaoMinutos
      );
    },
    isItemAtrasado(item) {
      return (
        this.minutosDecorridos(item) >=
        restauranteTempoLimites.producaoAtrasoMinutos
      );
    },
    itemTimestamp(item) {
      const value = this.itemStageDate(item);

      if (!value) {
        return Date.now();
      }

      const timestamp = new Date(value).getTime();
      return Number.isFinite(timestamp) ? timestamp : Date.now();
    },
    itemStageDate(item) {
      if (item.status === "pronto" && item.pronto_at) {
        return item.pronto_at;
      }

      if (item.status === "preparando" && item.preparando_at) {
        return item.preparando_at;
      }

      return (
        item.enviado_at ||
        item.created_at ||
        item.updated_at ||
        item.item?.created_at
      );
    },
    produtoDescricao(item) {
      return item.item && item.item.produto
        ? item.item.produto.descricao
        : `Produto #${item.item ? item.item.produto_id : "-"}`;
    },
    quantidadeItem(item) {
      if (!item.item) {
        return "-";
      }

      const unidade =
        item.item.produto &&
        item.item.produto.unidade_medida &&
        item.item.produto.unidade_medida.sigla
          ? item.item.produto.unidade_medida.sigla
          : "";

      return `${Number(item.item.quantidade || 0).toLocaleString(
        "pt-BR"
      )} ${unidade}`;
    },
    observacaoLinhas(item) {
      const observacao = String(item.observacao || "")
        .replace(/\s*\|\s*\[pedido_online[^\]]+\]/g, "")
        .replace(/\[pedido_online[^\]]+\]/g, "")
        .trim();

      if (!observacao) {
        return [];
      }

      return observacao
        .split(/\s*\|\s*/)
        .map((linha) => linha.trim())
        .filter(Boolean);
    },
    actionLabel(status, ticket = false) {
      const itemLabels = {
        preparando: "Iniciar preparo",
        pronto: "Marcar pronto",
        entregue: "Retirar este item",
      };
      const ticketLabels = {
        preparando: "Iniciar preparo",
        pronto: "Marcar ticket pronto",
        entregue: "Retirar da tela",
      };

      return ticket
        ? ticketLabels[status] || this.statusLabel(status)
        : itemLabels[status] || this.statusLabel(status);
    },
    statusLabel(status) {
      const labels = {
        pendente: "Pendente",
        preparando: "Preparando",
        pronto: "Pronto",
        entregue: "Retirado",
      };

      return labels[status] || status;
    },
    ticketActionClass(status) {
      const classes = {
        preparando: "btn-warning",
        pronto: "btn-primary",
        entregue: "btn-success",
      };

      return classes[status] || "btn-light-primary";
    },
    columnHelp(status) {
      const labels = {
        pendente: "Aguardando início",
        preparando: "Em execução",
        pronto: "Aguardando retirada",
      };

      return labels[status] || "Fluxo de produção";
    },
    itemCountLabel(value) {
      return quantityLabel(value, "item", "itens");
    },
    isTicketUpdating(ticket) {
      return !!this.updatingTicketKeys[ticket.key];
    },
    isItemUpdating(item) {
      return !!this.updatingItemIds[item.id];
    },
    tipoLabel(tipo) {
      const labels = {
        cozinha: "Cozinha",
        bar: "Bar",
        outro: "Outro",
      };

      return labels[tipo] || tipo;
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }

      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return "-";

      const today = new Date();
      const sameDay =
        date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate();

      return sameDay
        ? `às ${date.toLocaleTimeString("pt-BR", {
            hour: "2-digit",
            minute: "2-digit",
          })}`
        : `em ${date.toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "2-digit",
          })} às ${date.toLocaleTimeString("pt-BR", {
            hour: "2-digit",
            minute: "2-digit",
          })}`;
    },
  },
};
</script>

<style scoped>
/* Workspace operacional: ocupa a area util e mantem cada etapa independente. */
.restaurante-producao {
  height: calc(100vh - 100px);
  height: calc(100dvh - 100px);
  margin: -25px;
  max-height: calc(100vh - 100px);
  max-height: calc(100dvh - 100px);
  min-height: 560px;
  min-width: 0;
}

.kds-shell {
  background: #eef1f6;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: 0;
  overflow: hidden;
}

.kds-commandbar {
  align-items: center;
  background: #fff;
  border-bottom: 1px solid #e4e6ef;
  display: grid;
  flex: 0 0 auto;
  gap: 12px;
  grid-template-columns: minmax(190px, auto) minmax(0, 1fr);
  min-height: 60px;
  padding: 8px 12px;
}

.kds-station,
.kds-controls,
.kds-station > div,
.kds-column-header > div,
.kds-column-counts,
.producao-ticket-badges {
  align-items: center;
  display: flex;
}

.kds-station {
  gap: 10px;
  min-width: 0;
}

.kds-station > div,
.kds-column-header > div {
  align-items: flex-start;
  flex-direction: column;
  min-width: 0;
}

.kds-station strong {
  color: #181c32;
  font-size: 1.12rem;
  font-weight: 800;
  line-height: 1.2;
}

.kds-station small,
.kds-column-header small {
  color: #7e8299;
  font-size: 0.76rem;
  font-weight: 600;
  line-height: 1.3;
}

.kds-station-indicator {
  background: #1bc5bd;
  border: 4px solid #d8f6f3;
  border-radius: 50%;
  flex: 0 0 15px;
  height: 15px;
  width: 15px;
}

.kds-overdue-pill {
  align-items: center;
  background: #fff1f3;
  border-radius: 999px;
  color: #d9364f;
  display: inline-flex;
  font-size: 0.74rem;
  font-weight: 800;
  gap: 5px;
  margin-left: 4px;
  padding: 6px 9px;
  white-space: nowrap;
}

.kds-controls {
  gap: 8px;
  justify-content: flex-end;
  min-width: 0;
}

.kds-sector-filter {
  align-items: center;
  background: #f3f6f9;
  border: 1px solid transparent;
  border-radius: 8px;
  color: #5e6278;
  display: flex;
  flex: 1 1 260px;
  gap: 8px;
  margin: 0;
  max-width: 360px;
  min-height: 44px;
  padding: 0 10px;
}

.kds-sector-filter:focus-within {
  border-color: #3699ff;
  box-shadow: 0 0 0 3px rgba(54, 153, 255, 0.16);
}

.kds-sector-filter select {
  appearance: auto;
  background: transparent;
  border: 0;
  color: #181c32;
  flex: 1;
  font-weight: 700;
  height: 42px;
  min-width: 0;
  outline: 0;
}

.kds-auto-refresh,
.kds-icon-button {
  align-items: center;
  background: #f3f6f9;
  border: 1px solid transparent;
  border-radius: 8px;
  color: #5e6278;
  display: inline-flex;
  font-weight: 700;
  justify-content: center;
  min-height: 44px;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.kds-auto-refresh {
  gap: 7px;
  padding: 0 12px;
  white-space: nowrap;
}

.kds-auto-refresh.active {
  background: #e8f8f5;
  color: #0f806f;
}

.kds-live-dot {
  background: #a1a5b7;
  border-radius: 50%;
  height: 8px;
  width: 8px;
}

.kds-auto-refresh.active .kds-live-dot {
  background: #1bc5bd;
  box-shadow: 0 0 0 4px rgba(27, 197, 189, 0.14);
}

.kds-icon-button {
  flex: 0 0 44px;
  height: 44px;
  padding: 0;
  text-decoration: none;
  width: 44px;
}

.kds-auto-refresh:hover,
.kds-icon-button:hover {
  background: #e8edf5;
  color: #181c32;
}

.kds-auto-refresh:focus-visible,
.kds-icon-button:focus-visible,
.kds-stage-tabs button:focus-visible,
.producao-ticket-header:focus-visible,
.producao-ticket-actions .btn:focus-visible,
.producao-actions .btn:focus-visible {
  border-color: #3699ff;
  box-shadow: 0 0 0 3px rgba(54, 153, 255, 0.25);
  outline: 0;
}

.kds-icon-button:disabled {
  cursor: wait;
  opacity: 0.65;
}

.kds-last-refresh {
  color: #7e8299;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}

.kds-stage-tabs {
  background: #fff;
  border-bottom: 1px solid #e4e6ef;
  display: none;
  flex: 0 0 auto;
  gap: 6px;
  overflow-x: auto;
  padding: 8px 10px;
}

.kds-stage-tabs button {
  align-items: center;
  background: #f3f6f9;
  border: 1px solid transparent;
  border-radius: 8px;
  color: #5e6278;
  display: flex;
  flex: 1 0 auto;
  font-weight: 800;
  gap: 8px;
  justify-content: center;
  min-height: 44px;
  padding: 0 14px;
}

.kds-stage-tabs button span {
  background: #fff;
  border-radius: 999px;
  min-width: 24px;
  padding: 2px 7px;
}

.kds-stage-tabs button.active.is-pendente {
  background: #fff4dc;
  color: #9a6500;
}

.kds-stage-tabs button.active.is-preparando {
  background: #e8f3ff;
  color: #1264aa;
}

.kds-stage-tabs button.active.is-pronto {
  background: #eee8ff;
  color: #6542b5;
}

.kds-board {
  display: grid;
  flex: 1 1 auto;
  gap: 10px;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  min-height: 0;
  overflow: hidden;
  padding: 10px;
}

.kds-board-etapa-unica {
  grid-template-columns: minmax(0, 1fr);
}

.kds-column {
  background: #f7f8fb;
  border: 1px solid #dfe3eb;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  padding: 0;
}

.kds-column-header {
  align-items: center;
  background: #fff;
  border: 0;
  border-bottom: 3px solid #ffa800;
  border-left: 0;
  border-radius: 0;
  display: flex;
  flex: 0 0 auto;
  justify-content: space-between;
  margin: 0;
  min-height: 58px;
  padding: 8px 12px;
}

.kds-column-header > div:first-child {
  align-items: flex-start;
}

.kds-column-header > div:first-child strong {
  color: #181c32;
  font-size: 1rem;
  font-weight: 800;
}

.kds-column-header.kds-column-preparando {
  border-bottom-color: #3699ff;
}

.kds-column-header.kds-column-pronto {
  border-bottom-color: #8950fc;
}

.kds-column-counts {
  align-items: flex-end;
  flex-direction: column;
}

.kds-column-counts strong {
  color: #181c32;
  font-size: 1.12rem;
  line-height: 1;
}

.kds-column-counts span {
  background: transparent;
  color: #7e8299;
  font-size: 0.7rem;
  line-height: 1.25;
  min-width: auto;
  padding: 0;
}

.kds-column-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 8px;
  scrollbar-color: #c4c8d3 transparent;
  scrollbar-width: thin;
}

.kds-column-empty {
  align-items: center;
  color: #7e8299;
  display: flex;
  flex-direction: column;
  font-weight: 600;
  gap: 12px;
  justify-content: center;
  min-height: 190px;
  padding: 20px;
  text-align: center;
}

.kds-column-empty i {
  color: #b5b8c5;
  font-size: 2rem;
}

.kds-column-empty div {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.kds-column-empty strong {
  color: #5e6278;
}

.kds-column-empty span {
  font-size: 0.78rem;
}

.producao-ticket {
  background: #fff;
  border: 1px solid #e4e6ef;
  border-left: 5px solid #ffa800;
  border-radius: 9px;
  box-shadow: 0 2px 7px rgba(24, 28, 50, 0.06);
  display: flex;
  flex-direction: column;
  margin-bottom: 8px;
  padding: 10px;
}

.producao-card-preparando {
  border-left-color: #3699ff;
}

.producao-card-pronto {
  border-left-color: #8950fc;
}

.producao-card-atencao {
  box-shadow: 0 0 0 2px rgba(255, 168, 0, 0.22);
}

.producao-card-atrasado {
  border-color: #f64e60;
  box-shadow: 0 0 0 2px rgba(246, 78, 96, 0.17);
}

.producao-ticket-header {
  align-items: center;
  background: transparent;
  border: 0;
  color: #181c32;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  min-height: 44px;
  padding: 0;
  text-align: left;
  width: 100%;
}

.producao-ticket-identity {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-width: 0;
}

.producao-ticket-identity > span,
.producao-ticket-identity > small {
  color: #7e8299;
  font-size: 0.7rem;
  font-weight: 650;
}

.producao-ticket-identity strong {
  color: #181c32;
  font-size: 1rem;
  font-weight: 800;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.producao-ticket-badges {
  align-items: flex-end;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 4px;
}

.producao-ticket-summary,
.kds-timer {
  background: #f3f6f9;
  border-radius: 999px;
  color: #3f4254;
  font-size: 0.7rem;
  font-weight: 800;
  padding: 3px 7px;
  white-space: nowrap;
}

.kds-timer {
  color: #181c32;
}

.kds-timer.is-warning {
  background: #fff4dc;
  color: #9a6500 !important;
}

.kds-timer.is-danger {
  background: #fff1f3;
  color: #d9364f !important;
}

.producao-ticket-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
}

.producao-ticket-meta span {
  color: #7e8299;
  font-size: 0.7rem;
  font-weight: 600;
}

.producao-ticket-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.producao-ticket-actions .btn {
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 44px;
  width: 100%;
}

.producao-ticket-items {
  border-top: 1px solid #ebedf3;
  display: grid;
  gap: 8px;
  margin-top: 10px;
  padding-top: 9px;
}

.producao-comanda-group {
  display: grid;
  gap: 8px;
}

.producao-comanda-header {
  align-items: center;
  background: #f3f6f9;
  border-radius: 6px;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  padding: 6px 8px;
}

.producao-comanda-header strong {
  color: #181c32;
  font-size: 0.88rem;
  font-weight: 800;
}

.producao-comanda-header span {
  color: #7e8299;
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}

.producao-ticket-item {
  background: #fff;
  border: 1px solid #ebedf3;
  border-radius: 7px;
  padding: 9px;
}

.producao-produto {
  align-items: flex-start;
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: space-between;
}

.producao-produto strong {
  color: #181c32;
  font-size: 0.94rem;
  font-weight: 800;
  line-height: 1.25;
}

.producao-produto span {
  color: #7e8299;
  flex: 0 0 auto;
  font-weight: 700;
  white-space: nowrap;
}

.producao-observacao {
  background: #fff8e7;
  border: 1px solid #ffe3a1;
  border-radius: 6px;
  color: #6d4c00;
  display: flex;
  flex-direction: column;
  gap: 3px;
  font-weight: 750;
  line-height: 1.35;
  margin-top: 8px;
  padding: 8px;
}

.producao-observacao::before {
  content: "Observações";
  font-size: 0.66rem;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.producao-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.producao-actions .btn {
  min-height: 40px;
  width: 100%;
}

.producao-action-arrow {
  margin-left: 8px;
  transform: translateX(0);
  transition: transform 0.15s ease;
}

.producao-ticket-actions .btn:hover .producao-action-arrow,
.producao-actions .btn:hover .producao-action-arrow {
  transform: translateX(3px);
}

.restaurante-producao.is-fullscreen {
  background: #eef1f6;
  height: 100vh;
  height: 100dvh;
  margin: 0;
  max-height: 100vh;
  max-height: 100dvh;
  min-height: 100vh;
  min-height: 100dvh;
  width: 100vw;
}

@media (max-width: 1199.98px) {
  .kds-last-refresh {
    display: none;
  }

  .kds-commandbar {
    grid-template-columns: minmax(170px, auto) minmax(0, 1fr);
  }
}

@media (max-width: 991.98px) {
  .kds-stage-tabs {
    display: flex;
  }

  .kds-board,
  .kds-board-etapa-unica {
    display: block;
  }

  .kds-column {
    display: none;
    height: 100%;
  }

  .kds-column.is-mobile-active,
  .kds-board-etapa-unica .kds-column {
    display: flex;
  }

  .kds-auto-refresh {
    padding: 0 10px;
  }
}

@media (max-width: 767.98px) {
  .restaurante-producao {
    height: calc(100vh - 85px);
    height: calc(100dvh - 85px);
    margin: -15px;
    max-height: calc(100vh - 85px);
    max-height: calc(100dvh - 85px);
    min-height: 500px;
  }

  .kds-commandbar {
    gap: 7px;
    grid-template-columns: 1fr auto;
    padding: 7px 8px;
  }

  .kds-station small,
  .kds-overdue-pill,
  .kds-last-refresh,
  .kds-auto-refresh {
    display: none;
  }

  .kds-sector-filter {
    flex-basis: auto;
    max-width: 220px;
  }

  .kds-fullscreen-button {
    display: none;
  }

  .kds-board {
    padding: 7px;
  }
}

@media (max-width: 575.98px) {
  .kds-commandbar {
    grid-template-columns: auto minmax(0, 1fr);
  }

  .kds-station > div {
    display: none;
  }

  .kds-controls {
    gap: 5px;
  }

  .kds-sector-filter {
    max-width: none;
  }

  .kds-sector-filter i {
    display: none;
  }

  .kds-stage-tabs {
    padding: 6px 7px;
  }

  .kds-stage-tabs button {
    font-size: 0.76rem;
    min-height: 42px;
    padding: 0 9px;
  }

  .kds-column-header {
    min-height: 54px;
  }
}
</style>
